import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { FileText, ChevronRight, Code, AlignLeft, MessageSquare } from "lucide-react";
import { VoiceRecorder } from "./VoiceRecorder";

/**
 * TAQuestionRenderer — Renders TA-style questions with scenario, primary_question,
 * follow_up, response_fields, and optional code_block / supporting_data.
 *
 * Expected item shape:
 * {
 *   type: "question",
 *   id: "ta_p1_q1",
 *   question_number: 1,
 *   phase: 1,
 *   title: "Advanced Phishing Indicator Analysis",
 *   scenario: "Your email security gateway flags...",
 *   code_block: { language: "text", content: "From: ..." },          // optional
 *   supporting_data: [{ heading: "...", content: "..." }],            // optional
 *   primary_question: "Identify at least four indicators...",
 *   follow_up: "The claims adjuster clicked the link...",
 *   response_fields: [
 *     { id: "p1q1_primary", label: "Phishing Indicators Analysis", type: "long_text", max_words: 400 },
 *     { id: "p1q1_followup", label: "Post-Click Response Actions", type: "long_text", max_words: 300 }
 *   ],
 *   expected_time_range: { ... },         // NOT displayed
 *   scoring_dimensions: [ ... ]           // NOT displayed
 * }
 */
export function TAQuestionRenderer({ item, index, responses, onResponseChange, onPaste, voiceEnabled, token, sectionId, locked }) {
  const id = item.id || `ta_question_${index}`;
  const title = item.title || `Question ${item.question_number || index + 1}`;
  const scenario = item.scenario || "";
  const primaryQuestion = item.primary_question || "";
  const followUp = item.follow_up || "";
  const responseFields = item.response_fields || [];
  const codeBlock = item.code_block || null;
  const supportingData = item.supporting_data || [];
  const phase = item.phase;
  const qNum = item.question_number;

  const wordCount = (text) => {
    if (!text || !text.trim()) return 0;
    return text.trim().split(/\s+/).length;
  };

  return (
    <div key={index} className={`my-8 ${locked ? "opacity-60" : ""}`} style={locked ? { pointerEvents: 'none' } : {}} data-testid={`ta-question-${id}`}>
      {/* Question Header */}
      <div className="bg-gradient-to-r from-[#1B3A5C] to-[#2a5a8c] text-white px-5 py-4 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded bg-white/10 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold">{qNum || "Q"}</span>
          </div>
          <div>
            <h3 className="font-semibold text-base">{title}</h3>
            {phase && (
              <span className="text-xs text-white/60">Phase {phase}</span>
            )}
          </div>
        </div>
      </div>

      <div className="border border-t-0 border-[#1B3A5C]/20 rounded-b-lg bg-white">
        {/* Scenario */}
        {scenario && (
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-[#00AEEF]" />
              <span className="text-xs font-semibold text-[#1B3A5C] uppercase tracking-wider">Scenario</span>
            </div>
            <div className="text-sm text-[#3b3b3b] leading-relaxed whitespace-pre-line bg-gray-50 rounded-lg p-4 border border-gray-100">
              {scenario}
            </div>
          </div>
        )}

        {/* Code Block (optional — read-only, syntax highlighted) */}
        {codeBlock && (
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <Code className="w-4 h-4 text-[#00AEEF]" />
              <span className="text-xs font-semibold text-[#1B3A5C] uppercase tracking-wider">
                {codeBlock.language || "Reference"}
              </span>
            </div>
            <pre className="bg-[#1e1e2e] text-gray-200 rounded-lg p-4 overflow-x-auto text-[13px] leading-6 font-mono border border-[#2a2a3e]">
              <code>{codeBlock.content || codeBlock.code || ""}</code>
            </pre>
          </div>
        )}

        {/* Supporting Data (optional) */}
        {supportingData.length > 0 && (
          <div className="px-5 py-4 border-b border-gray-100">
            {supportingData.map((section, sIdx) => (
              <div key={sIdx} className="mb-3 last:mb-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <ChevronRight className="w-4 h-4 text-[#00AEEF]" />
                  <h4 className="font-semibold text-sm text-[#1B3A5C]">{section.heading || section.title}</h4>
                </div>
                <div className={`ml-6 text-sm text-[#3b3b3b] leading-relaxed ${section.format === "monospace" ? "font-mono bg-[#1e1e2e] text-gray-200 rounded p-3 text-xs" : "whitespace-pre-line bg-gray-50 rounded p-3 border border-gray-100"}`}>
                  {section.content}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Primary Question */}
        {primaryQuestion && (
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="w-4 h-4 text-[#00AEEF]" />
              <span className="text-xs font-semibold text-[#1B3A5C] uppercase tracking-wider">Question</span>
            </div>
            <p className="text-sm font-medium text-[#1B3A5C] leading-relaxed">{primaryQuestion}</p>
          </div>
        )}

        {/* Follow-Up */}
        {followUp && (
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <ChevronRight className="w-4 h-4 text-amber-500" />
              <span className="text-xs font-semibold text-amber-600 uppercase tracking-wider">Follow-up</span>
            </div>
            <p className="text-sm text-[#3b3b3b] leading-relaxed">{followUp}</p>
          </div>
        )}

        {/* Response Fields */}
        {responseFields.length > 0 && (
          <div className="px-5 py-4">
            <div className="space-y-5">
              {responseFields.map((field, fIdx) => {
                const fieldKey = field.id || `${id}_field_${fIdx}`;
                const fieldValue = responses[fieldKey] || "";
                const maxWords = field.max_words || 0;
                const currentWords = wordCount(fieldValue);
                const isOverLimit = maxWords > 0 && currentWords > maxWords;
                const fieldType = field.type || "long_text";

                return (
                  <div key={fIdx} data-testid={`ta-response-field-${fieldKey}`} style={{ position: 'relative', zIndex: 1 }}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-[#00AEEF]/10 flex items-center justify-center flex-shrink-0">
                          {fieldType === "code" ? (
                            <Code className="w-3.5 h-3.5 text-[#00AEEF]" />
                          ) : (
                            <AlignLeft className="w-3.5 h-3.5 text-[#00AEEF]" />
                          )}
                        </div>
                        <label className="text-sm font-medium text-[#3b3b3b]">{field.label}</label>
                      </div>
                      {maxWords > 0 && (
                        <span className={`text-xs font-mono ${isOverLimit ? "text-red-500 font-semibold" : "text-gray-400"}`}>
                          {currentWords} / {maxWords} words
                        </span>
                      )}
                    </div>

                    {fieldType === "code" ? (
                      <div className="bg-[#1e1e2e] rounded-lg border border-[#2a2a3e] overflow-hidden">
                        <textarea
                          value={fieldValue}
                          onChange={(e) => !locked && onResponseChange(fieldKey, e.target.value)}
                          onPaste={(e) => {
                            const pasted = e.clipboardData?.getData("text") || "";
                            if (pasted && onPaste && !locked) onPaste(fieldKey, pasted);
                          }}
                          placeholder="Write your code here..."
                          spellCheck={false}
                          readOnly={locked}
                          className="w-full bg-transparent text-gray-200 font-mono text-[13px] leading-6 p-3 resize-none outline-none placeholder:text-gray-600 min-h-[150px]"
                          style={{ pointerEvents: locked ? 'none' : 'auto' }}
                          data-testid={`ta-code-input-${fieldKey}`}
                        />
                      </div>
                    ) : (
                      <Textarea
                        value={fieldValue}
                        onChange={(e) => !locked && onResponseChange(fieldKey, e.target.value)}
                        onPaste={(e) => {
                          const pasted = e.clipboardData?.getData("text") || "";
                          if (pasted && onPaste && !locked) onPaste(fieldKey, pasted);
                        }}
                        placeholder={`Enter your response for: ${field.label}...`}
                        readOnly={locked}
                        className={`${fieldType === "short_text" ? "min-h-[60px]" : "min-h-[120px]"} text-sm ${isOverLimit ? "border-red-300 focus:ring-red-200" : ""}`}
                        style={{ pointerEvents: locked ? 'none' : 'auto' }}
                        data-testid={`ta-text-input-${fieldKey}`}
                      />
                    )}
                    {voiceEnabled && (
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-gray-400 italic">or</span>
                        <VoiceRecorder
                          token={token}
                          questionId={fieldKey}
                          sectionId={sectionId}
                          onTranscription={(text) => {
                            const existing = responses[fieldKey] || "";
                            if (!existing.trim()) {
                              onResponseChange(fieldKey, text);
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
